import useSWR from 'swr';
import qs from 'qs';
import useSWRInfinite from 'swr/infinite';
import {
  ISubmissionSearchResultResponse,
  ISubmissionSearchResult
} from 'typings/submission';
import { ITEMS_PER_PAGE } from 'containers/Search/Sessions/SearchSessions';
import { IProceedingsSearchResultResponse } from 'typings/proceedings';
import { ICfaSearchResultResponse } from 'typings/callForAbstract';
import { ICfpSearchResultResponse } from 'typings/callForPaper';
import { IEventSearchResultResponse } from 'typings/event';
import { ISessionSearchResultResponse } from 'typings/session';

// fetch CFA data
export const useCallForAbstracts = (
  inputTerm: string,
  offset: number,
  itemsPerPage: number
) => {
  const { data, error, ...rest } = useSWR<ICfaSearchResultResponse>([
    `/home-search/home/v1/cfa?query=${inputTerm}&offset=${offset}&limit=${itemsPerPage}`,
    true // require auth token
  ]);

  return {
    ...rest,
    data,
    error,
    hasError: Boolean(error),
    isLoading: !data && !error
  };
};

// fetch CFP data
export const useCallForPapers = (
  inputTerm: string,
  offset: number,
  itemsPerPage: number
) => {
  const { data, error, ...rest } = useSWR<ICfpSearchResultResponse>([
    `/home-search/home/v1/cfp?query=${inputTerm}&offset=${offset}&limit=${itemsPerPage}`,
    true // require auth token
  ]);

  return {
    ...rest,
    data,
    error,
    hasError: Boolean(error),
    isLoading: !data && !error
  };
};

export const useEvents = (
  inputTerm: string,
  currentPage: number,
  itemsPerPage: number
) => {
  const { data, error, ...rest } = useSWR<IEventSearchResultResponse>([
    `/home-search/home/v1/events?query=${inputTerm}&offset=${
      currentPage * itemsPerPage
    }&limit=${itemsPerPage}`,
    true // require auth token
  ]);

  const events = {
    ...data,
    data: data?.data.map(event => ({
      ...event,
      bannerUrl:
        event.bannerUrl || '/home-client/shared/event-placeholder-bg.svg'
    }))
  };

  return {
    ...rest,
    data: events,
    error,
    hasError: Boolean(error),
    isLoading: !data && !error
  };
};

export const useProceedings = (
  inputTerm: string,
  currentPage: number,
  itemsPerPage: number,
  isEnabled = false
) => {
  const url = `/home-search/home/v1/proceedings${qs.stringify(
    {
      query: inputTerm,
      offset: currentPage * itemsPerPage,
      limit: itemsPerPage
    },
    {
      addQueryPrefix: true
    }
  )}`;

  const swrParams = isEnabled ? [url, true] : null;

  const { data, error, ...rest } =
    useSWR<IProceedingsSearchResultResponse>(swrParams);

  return {
    ...rest,
    data: data,
    error,
    hasError: Boolean(error),
    isLoading: !data && !error
  };
};

export const useSessions = (
  inputTerm: string,
  currentPage: number,
  itemsPerPage: number
) => {
  const { data, error, ...rest } = useSWR<ISessionSearchResultResponse>([
    `/home-search/home/v1/sessions?query=${inputTerm}&offset=${
      currentPage * itemsPerPage
    }&limit=${itemsPerPage}`,
    true // require auth token
  ]);

  return {
    ...rest,
    data,
    error,
    hasError: Boolean(error),
    isLoading: !data && !error
  };
};

interface ISubmissionRequestData {
  inputTerm?: string;
  page: number;
  itemsPerPage: number;
  demo?: boolean;
  embargoed?: boolean;
}

export const useSubmissions = (submissionData: ISubmissionRequestData) => {
  const { itemsPerPage, page, inputTerm } = submissionData;
  const url = `/home-search/home/v1/submissions${qs.stringify(
    {
      query: inputTerm,
      offset: page * itemsPerPage,
      limit: itemsPerPage
    },
    {
      addQueryPrefix: true
    }
  )}`;

  const { data, error, ...rest } = useSWR<ISubmissionSearchResultResponse>([
    url,
    true // require auth token
  ]);

  return {
    ...rest,
    data: data,
    error,
    hasError: Boolean(error),
    isLoading: !data && !error
  };
};

export const useInfiniteSubmissions = (
  submissionData: ISubmissionRequestData
) => {
  const { itemsPerPage, inputTerm } = submissionData;
  const getKey = (pageIndex: number) => {
    return [
      `/home-search/home/v1/submissions${qs.stringify(
        {
          query: inputTerm,
          offset: pageIndex * itemsPerPage,
          limit: itemsPerPage,
          demo: false,
          embargoed: false
        },
        {
          addQueryPrefix: true
        }
      )}`,
      true // require auth token
    ];
  };

  const { data, error, size, setSize, ...rest } =
    useSWRInfinite<ISubmissionSearchResult[]>(getKey);

  const isLoadingInitialData = !data && !error;
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && data && typeof data[size - 1] === 'undefined') ||
    false;
  const isEmpty = data?.[0]?.length === 0;
  const isReachingEnd =
    isEmpty ||
    (data && data[data.length - 1]?.length < ITEMS_PER_PAGE) ||
    false;

  return {
    ...rest,
    data: data
      ? data.reduce((acc, curr: any) => [...acc, ...curr.data], [])
      : [],
    error,
    isLoading: isLoadingInitialData,
    isLoadingMore,
    isReachingEnd,
    setSize,
    size
  };
};

export const useAllResearch = (userId: string) => {
  //@Note: make these paramaters offset, limit, filters, demo, embargoed dynamic in future
  const { data, error, ...rest } = useSWR<ISubmissionSearchResultResponse>([
    `home-search/home/v2/submissions?offset=0&limit=100&filters=authors=${userId},demo=0,embargoed=0`
  ]);

  return {
    ...rest,
    data: data?.data || [],
    error,
    hasError: Boolean(error),
    isLoading: !data && !error
  };
};
